import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getCompanies,
  getActiveCompanies,
  createCompany,
  deleteCompany,
  updateCompany,
  updateCompanyStatus,
  type Company,
  type CreateCompanyRequest,
  type UpdateCompanyStatusRequest,
} from "@/services/api/companies";

const companiesKeys = {
  all: ["companies"] as const,
  lists: () => [...companiesKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...companiesKeys.lists(), { filters }] as const,
  details: () => [...companiesKeys.all, "detail"] as const,
  detail: (id: string | number) => [...companiesKeys.details(), id] as const,
};

export function useCompanies(
  options?: Omit<UseQueryOptions<Company[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<Company[], Error>({
    queryKey: companiesKeys.list(),
    queryFn: getCompanies,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useActiveCompanies(
  options?: Omit<UseQueryOptions<Company[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<Company[], Error>({
    queryKey: [...companiesKeys.all, "active"],
    queryFn: getActiveCompanies,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation<Company, Error, CreateCompanyRequest>({
    mutationFn: createCompany,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
      toast.success("Company created", {
        description: `"${data.name ?? ""}" has been successfully created.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create company", {
        description:
          error.message || "An error occurred while creating company.",
      });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
      toast.success("Company deleted", {
        description: "The company has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete company", {
        description:
          error.message || "An error occurred while deleting company.",
      });
    },
  });
}

export function useUpdateCompanyStatus() {
  const queryClient = useQueryClient();
  return useMutation<
    Company,
    Error,
    { id: string | number; data: UpdateCompanyStatusRequest }
  >({
    mutationFn: ({ id, data }) => updateCompanyStatus(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
      toast.success("Company status updated", {
        description: `"${data.name ?? ""}" has been ${data.isActive ? "activated" : "deactivated"}.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update company status", {
        description:
          error.message || "An error occurred while updating company status.",
      });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation<
    Company,
    Error,
    { id: string | number; data: CreateCompanyRequest }
  >({
    mutationFn: ({ id, data }) => updateCompany(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
      toast.success("Company updated", {
        description: `"${data.name ?? ""}" has been successfully updated.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update company", {
        description:
          error.message || "An error occurred while updating company.",
      });
    },
  });
}

export { companiesKeys };
